import React from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import { Card, Badge, Button, Input, Select } from '@/components/ui';
import type { CompanyFilter, PoolOption, CompanyGroup } from '@/types/companyManagement.types';

const FormikAutoSync: React.FC<{
  onFiltersChange: (filters: CompanyFilter) => void;
}> = ({ onFiltersChange }) => {
  const { values } = useFormikContext<{
    search: string;
    poolId: string;
    groupCode: string;
    isInactive: string;
  }>();

  React.useEffect(() => {
    const newFilters: CompanyFilter = {
      search: values.search || undefined,
      poolId: values.poolId ? parseInt(values.poolId) : undefined,
      groupCode: values.groupCode || undefined,
      isInactive:
        values.isInactive === 'true' ? true : values.isInactive === 'false' ? false : undefined,
    };
    onFiltersChange(newFilters);
  }, [values, onFiltersChange]);

  return null;
};

interface CompanyFiltersProps {
  filters: CompanyFilter;
  onFiltersChange: (filters: CompanyFilter) => void;
  pools: PoolOption[];
  groups: CompanyGroup[];
  onClearFilters: () => void;
  inDrawer?: boolean;
}

export const CompanyFilters: React.FC<CompanyFiltersProps> = ({
  filters,
  onFiltersChange,
  pools,
  groups,
  onClearFilters,
  inDrawer = false,
}) => {
  const initialValues = {
    search: filters.search || '',
    poolId: filters.poolId?.toString() || '',
    groupCode: filters.groupCode || '',
    isInactive: filters.isInactive === true ? 'true' : filters.isInactive === false ? 'false' : '',
  };

  const hasActiveFilters = Object.values(filters).some(
    (value) => value !== undefined && value !== ''
  );

  const poolOptions = [
    { value: '', label: 'All pools' },
    ...pools.map((p) => ({ value: p.poolId.toString(), label: p.pool })),
  ];

  const groupOptions = [
    { value: '', label: 'All roles' },
    ...groups.map((g) => ({ value: g.code, label: g.name })),
  ];

  const statusOptions = [
    { value: '', label: 'All statuses' },
    { value: 'false', label: 'Active' },
    { value: 'true', label: 'Inactive' },
  ];

  return (
    <Card
      title={inDrawer ? undefined : 'Filters'}
      className={
        inDrawer ? 'rounded-none border-0 bg-transparent shadow-none dark:bg-transparent' : ''
      }
      noPadding={inDrawer}
      footer={
        !inDrawer && hasActiveFilters ? (
          <Button variant="ghost" size="sm" onClick={onClearFilters}>
            Clear all filters
          </Button>
        ) : null
      }
    >
      <Formik initialValues={initialValues} enableReinitialize onSubmit={() => {}}>
        {({ values, setFieldValue }) => {
          const handleClearFilter = (key: keyof typeof values) => {
            setFieldValue(key, '');
          };

          return (
            <Form>
              <FormikAutoSync onFiltersChange={onFiltersChange} />
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Input
                  name="search"
                  label="Search"
                  placeholder="Search by name..."
                  leftIcon={
                    <svg
                      className="h-4 w-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  }
                />

                <Select name="poolId" label="Pool" options={poolOptions} />

                <Select name="groupCode" label="Role" options={groupOptions} />

                <Select name="isInactive" label="Status" options={statusOptions} />
              </div>

              {hasActiveFilters && !inDrawer && (
                <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-600">
                  <div className="flex flex-wrap gap-2">
                    {filters.search && (
                      <Badge variant="primary" size="sm">
                        Search: {filters.search}
                        <button
                          type="button"
                          onClick={() => handleClearFilter('search')}
                          className="ml-1 inline-flex h-4 w-4 items-center justify-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                          aria-label="Remove search filter"
                        >
                          ×
                        </button>
                      </Badge>
                    )}

                    {filters.poolId !== undefined && (
                      <Badge variant="success" size="sm">
                        Pool: {pools.find((p) => p.poolId === filters.poolId)?.pool}
                        <button
                          type="button"
                          onClick={() => handleClearFilter('poolId')}
                          className="ml-1 inline-flex h-4 w-4 items-center justify-center text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                          aria-label="Remove pool filter"
                        >
                          ×
                        </button>
                      </Badge>
                    )}

                    {filters.groupCode && (
                      <Badge variant="default" size="sm">
                        Role: {groups.find((g) => g.code === filters.groupCode)?.name}
                        <button
                          type="button"
                          onClick={() => handleClearFilter('groupCode')}
                          className="ml-1 inline-flex h-4 w-4 items-center justify-center text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
                          aria-label="Remove role filter"
                        >
                          ×
                        </button>
                      </Badge>
                    )}

                    {filters.isInactive !== undefined && (
                      <Badge variant="warning" size="sm">
                        Status: {filters.isInactive ? 'Inactive' : 'Active'}
                        <button
                          type="button"
                          onClick={() => handleClearFilter('isInactive')}
                          className="ml-1 inline-flex h-4 w-4 items-center justify-center text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300"
                          aria-label="Remove status filter"
                        >
                          ×
                        </button>
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </Card>
  );
};
